import { API_CALL_APPROVAL_TIMEOUT_MS } from '@erp/rpc';
import { rpcClient } from '@/api/rpc/client';
import { useAgentState } from '@/context/agent-state.context';
import {
  ApiCallApprovalCard,
  type ApiCallApprovalRequest,
} from './ApiCallApprovalCard';

type ServerStatus = 'approved' | 'rejected' | 'timed_out';

type PendingCard = ApiCallApprovalRequest & {
  serverStatus?: ServerStatus;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function statusFromResult(result: unknown): ServerStatus | undefined {
  if (!isRecord(result)) return undefined;
  const status = result.status;
  return status === 'approved' ||
    status === 'rejected' ||
    status === 'timed_out'
    ? status
    : undefined;
}

export function ApiCallApprovalPanel() {
  const { activeToolCalls } = useAgentState();
  const pendingCards = activeToolCalls.flatMap<PendingCard>((toolCall) => {
    if (toolCall.toolName !== 'call_api' || !toolCall.isWrite) return [];
    const endpointId = toolCall.args.endpointId;
    const args = toolCall.args.args;
    const reason = toolCall.args.reason;
    if (
      typeof endpointId !== 'string' ||
      !isRecord(args) ||
      typeof reason !== 'string'
    ) {
      return [];
    }
    return [
      {
        toolCallId: toolCall.toolCallId,
        endpointId,
        args,
        reason,
        expiresAt: (toolCall.createdAt ?? 0) + API_CALL_APPROVAL_TIMEOUT_MS,
        serverStatus: statusFromResult(toolCall.result),
      },
    ];
  });

  if (pendingCards.length === 0) return null;

  return (
    <div className="space-y-2 border-t px-3 py-2">
      {pendingCards.map((card) => (
        <ApiCallApprovalCard
          key={card.toolCallId}
          request={card}
          serverStatus={card.serverStatus}
          onApprove={(pendingCallId) =>
            rpcClient.ai.chatAgent.approveApiCall({ pendingCallId })
          }
          onReject={(pendingCallId) =>
            rpcClient.ai.chatAgent.rejectApiCall({ pendingCallId })
          }
        />
      ))}
    </div>
  );
}
